import { formatInTimeZone } from "date-fns-tz";

export const UK_TIMEZONE = "Europe/London";

/**
 * Get today's date in UK timezone as "YYYY-MM-DD" string.
 * Safe regardless of the developer's/user's local timezone.
 */
export function getUkTodayStr(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: UK_TIMEZONE,
  }).format(new Date());
}

/**
 * Get tomorrow's date in UK timezone as "YYYY-MM-DD" string.
 */
export function getUkTomorrowStr(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: UK_TIMEZONE,
  }).format(tomorrow);
}

/**
 * Convert a Date object to a "YYYY-MM-DD" string in UK timezone.
 * Use this for datetime values (createdAt, scheduledDate, etc.).
 */
export function toUkDateStr(date: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: UK_TIMEZONE,
  }).format(date);
}

/**
 * Get current UK time as "HH:MM" (24h format).
 */
export function getUkTimeNow(): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: UK_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date());
}

/**
 * Check if a date is today in UK timezone.
 * Accepts both Date objects and "YYYY-MM-DD" strings.
 */
export function isUkToday(date: Date | string): boolean {
  const dateStr = typeof date === "string" ? date : toUkDateStr(date);
  return dateStr === getUkTodayStr();
}

/**
 * Check if a date is tomorrow in UK timezone.
 * Accepts both Date objects and "YYYY-MM-DD" strings.
 */
export function isUkTomorrow(date: Date | string): boolean {
  const dateStr = typeof date === "string" ? date : toUkDateStr(date);
  return dateStr === getUkTomorrowStr();
}

/**
 * Parse a "YYYY-MM-DD" date string into a Date at noon local time.
 * Avoids the UTC-midnight pitfall of `new Date("YYYY-MM-DD")` which can
 * shift the day in positive-offset timezones when used with local-time APIs.
 */
export function parseDateStr(dateStr: string): Date {
  return new Date(dateStr + "T12:00:00");
}

/**
 * Get today's date in UK timezone as a Date object (at noon local time).
 */
export function getUkToday(): Date {
  return parseDateStr(getUkTodayStr());
}

/**
 * Parse a date-only or ISO date-time string into a Date at LOCAL midnight on
 * that calendar day. Use when comparing against react-day-picker day cells,
 * which are themselves local-midnight Dates — `new Date(iso)` on a `@db.Date`
 * value (serialized as "YYYY-MM-DDT00:00:00.000Z") yields a UTC-midnight instant
 * that is off by a day against a local-midnight cell in non-UTC timezones.
 */
export function parseApiDateToLocalMidnight(iso: string): Date {
  const parts = iso.slice(0, 10).split("-");
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  return new Date(year, month - 1, day);
}

/**
 * Check if a date is after today and within the given number of days (UK timezone).
 * Accepts both Date objects and "YYYY-MM-DD" strings.
 */
export function isWithinUpcomingDays(
  date: Date | string,
  days: number,
): boolean {
  const dateStr = typeof date === "string" ? date : toUkDateStr(date);
  const todayStr = getUkTodayStr();
  if (dateStr <= todayStr) return false;

  const endDate = new Date();
  endDate.setDate(endDate.getDate() + days);
  const endStr = new Intl.DateTimeFormat("en-CA", {
    timeZone: UK_TIMEZONE,
  }).format(endDate);

  return dateStr <= endStr;
}

/**
 * Check if a date string is before today in UK timezone.
 * For "YYYY-MM-DD" strings, direct string comparison works.
 */
export function isBeforeUkToday(dateStr: string): boolean {
  return dateStr < getUkTodayStr();
}

/**
 * Format a date in UK timezone with the given format string
 */
export function formatUkDate(date: Date, formatStr: string): string {
  return formatInTimeZone(date, UK_TIMEZONE, formatStr);
}
